/**
 * Subliminal binaural engine ("erotic brain sync").
 *
 * Turns the subliminal voice itself into the binaural-beat carrier: the same
 * vocal is delivered to both ears with a constant frequency offset between
 * them (left −binHz/2, right +binHz/2). The auditory brainstem resolves the
 * interaural difference as a beat at exactly binHz (e.g. theta 6), so the
 * entrainment signal rides ON the suggestion instead of on a separate sine.
 *
 * DSP notes — why this is a frequency SHIFTER and not a pitch shifter:
 * a pitch shifter scales all frequencies multiplicatively, so the interaural
 * difference varies with voice pitch and the "beat" smears. A Bode/Weaver
 * single-sideband shifter adds a constant Hz offset to every component, which
 * is the only way to get a stable, lockable beat frequency from broadband
 * speech. We build the analytic signal with the classic pair of 4-section
 * IIR allpass cascades (Olli Niemitalo's coefficients, ~90° apart across the
 * audio band), then heterodyne with a quadrature oscillator per ear.
 *
 * A beat-locked amplitude tremolo (dip once per beat cycle, coherent in both
 * ears) is layered on so listeners on speakers/mono — where interaural beats
 * cannot form — still receive a monaural theta-rate modulation.
 *
 * The worklet is inlined and loaded through a Blob URL so the esbuild
 * single-bundle pipeline needs no extra emitted assets or server routes.
 */

import { resolveSubliminalGain } from "./subliminal-level.js";

const WORKLET_NAME = "subliminal-binaural-shifter";

const WORKLET_SOURCE = `
class SubliminalBinauralShifter extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [
      { name: "shiftHz", defaultValue: 6, minValue: 0, maxValue: 40, automationRate: "k-rate" },
      // Optional slow trajectory for the interaural difference. The base
      // beat remains bounded by shiftHz; this is a reversible laboratory
      // control, not a claim that a changing beat entrains the brain.
      { name: "beatRateTrajectoryDepthHz", defaultValue: 0, minValue: 0, maxValue: 5, automationRate: "k-rate" },
      { name: "beatRateTrajectoryPeriodSeconds", defaultValue: 45, minValue: 30, maxValue: 60, automationRate: "k-rate" },
      // Modest depth: at near-threshold levels a strong 6 Hz pulse is itself
      // an attention cue; shallow modulation entrains without flagging.
      { name: "amDepth", defaultValue: 0.22, minValue: 0, maxValue: 0.9, automationRate: "k-rate" },
      // Common spectral relocation applied to BOTH ears on top of the
      // differential beat split. 0 = in place (speech stays intelligible),
      // negative = "dark" disguise (content slides down under the music bed
      // and stops reading as its source sound), large positive = "silent"
      // near-ultrasonic carrier.
      { name: "carrierShiftHz", defaultValue: 0, minValue: -2000, maxValue: 16000, automationRate: "k-rate" },
      { name: "breathCycleSeconds", defaultValue: 6, minValue: 4, maxValue: 20, automationRate: "k-rate" },
      { name: "breathDepth", defaultValue: 0.16, minValue: 0, maxValue: 0.5, automationRate: "k-rate" },
      { name: "waveShape", defaultValue: 0, minValue: 0, maxValue: 3, automationRate: "k-rate" },
      { name: "phaseOffsetDegrees", defaultValue: 0, minValue: -180, maxValue: 180, automationRate: "k-rate" },
      { name: "stereoDelayMs", defaultValue: 0, minValue: 0, maxValue: 30, automationRate: "k-rate" },
      { name: "crossfeed", defaultValue: 0.35, minValue: 0, maxValue: 0.45, automationRate: "k-rate" },
      { name: "bassPulseHz", defaultValue: 0.6, minValue: 0.2, maxValue: 2, automationRate: "k-rate" },
      { name: "bassRippleDepth", defaultValue: 0.12, minValue: 0, maxValue: 0.3, automationRate: "k-rate" },
      // Envelope-following entrainment: instead of a fixed sine LFO, the
      // amplitude contour of the source (e.g. the moans/cries) becomes the
      // ripple/bulge pulse. 0 = pure fixed LFO, 1 = pure source envelope.
      { name: "envelopeEntrainment", defaultValue: 0, minValue: 0, maxValue: 1, automationRate: "k-rate" },
      { name: "envelopeAttackMs", defaultValue: 12, minValue: 1, maxValue: 200, automationRate: "k-rate" },
      { name: "envelopeReleaseMs", defaultValue: 220, minValue: 20, maxValue: 2000, automationRate: "k-rate" },
      // Covert masking: a slow, bounded "bulge" LFO on the common carrier that
      // swells and recedes the combined base so the content breathes under the
      // music bed instead of sitting flat. Kept shallow so it never flags.
      { name: "covertMaskDepth", defaultValue: 0, minValue: 0, maxValue: 0.5, automationRate: "k-rate" },
      { name: "covertMaskHz", defaultValue: 0.15, minValue: 0.05, maxValue: 0.5, automationRate: "k-rate" },
      // Phase coherence (Black Book §5.5): when >0, the bass pulse and covert
      // mask are derived from the SAME master phase as the binaural beat, so
      // every layer crests together. This reinforces a single frequency-
      // following response instead of letting independent oscillators drift
      // and fight each other. 0 = independent (legacy), 1 = fully locked.
      { name: "phaseCoherence", defaultValue: 0, minValue: 0, maxValue: 1, automationRate: "k-rate" },
    ];
  }

  constructor() {
    super();
    // Hilbert pair: two parallel allpass cascades whose outputs sit ~90 deg
    // apart from 20 Hz to 20 kHz. Coefficients: Olli Niemitalo.
    this.coeffsI = [0.6923877778065, 0.9360654322959, 0.9882295226860, 0.9987488452737].map((a) => a * a);
    this.coeffsQ = [0.4021921162426, 0.8561710882420, 0.9722909545651, 0.9952884791278].map((a) => a * a);
    this.stateI = this.coeffsI.map(() => ({ x1: 0, x2: 0, y1: 0, y2: 0 }));
    this.stateQ = this.coeffsQ.map(() => ({ x1: 0, x2: 0, y1: 0, y2: 0 }));
    this.qDelay = 0;      // Q path carries one extra sample of delay.
    this.phaseCommon = 0; // carrier relocation phase (shared by both ears)
    this.phaseDiff = 0;   // differential phase; interaural beat phase = 2*phaseDiff.
    this.beatRateTrajectoryPhase = 0;
    this.breathPhase = 0; // low -> 3 s rise -> 3 s fall at the default cycle.
    this.bassPhase = 0;   // shared scene-BPM crest carried by subliminal audio.
    this.covertPhase = 0; // slow covert-mask bulge phase.
    this.envelope = 0;    // smoothed source amplitude envelope (0..1).
    this.delayBuffer = new Float32Array(4096);
    this.delayIndex = 0;
  }

  runCascade(sample, coeffs, states) {
    let value = sample;
    for (let i = 0; i < coeffs.length; i += 1) {
      const c = coeffs[i];
      const s = states[i];
      const y = c * (value + s.y2) - s.x2;
      s.x2 = s.x1; s.x1 = value;
      s.y2 = s.y1; s.y1 = y;
      value = y;
    }
    return value;
  }

  process(inputs, outputs, parameters) {
    const output = outputs[0];
    const left = output[0];
    const right = output.length > 1 ? output[1] : output[0];
    const input = inputs[0];
    if (!input || input.length === 0) {
      left.fill(0);
      if (right !== left) right.fill(0);
      return true; // stay alive across cue gaps
    }
    const shiftHz = parameters.shiftHz[0];
    const beatRateTrajectoryDepthHz = parameters.beatRateTrajectoryDepthHz[0];
    const beatRateTrajectoryPeriodSeconds = Math.max(30, parameters.beatRateTrajectoryPeriodSeconds[0]);
    const amDepth = parameters.amDepth[0];
    const carrierShiftHz = parameters.carrierShiftHz[0];
    const breathCycleSeconds = Math.max(4, parameters.breathCycleSeconds[0]);
    const breathDepth = parameters.breathDepth[0];
    const waveShape = Math.round(parameters.waveShape[0]);
    const phaseOffset = (parameters.phaseOffsetDegrees[0] * Math.PI) / 180;
    const stereoDelaySamples = Math.max(0, Math.min(
      this.delayBuffer.length - 1,
      Math.round(parameters.stereoDelayMs[0] * sampleRate / 1000),
    ));
    const crossfeed = parameters.crossfeed[0];
    const bassPulseHz = parameters.bassPulseHz[0];
    const bassRippleDepth = parameters.bassRippleDepth[0];
    const envelopeEntrainment = Math.max(0, Math.min(1, parameters.envelopeEntrainment[0]));
    const envelopeAttackMs = Math.max(1, parameters.envelopeAttackMs[0]);
    const envelopeReleaseMs = Math.max(20, parameters.envelopeReleaseMs[0]);
    const covertMaskDepth = Math.max(0, Math.min(0.5, parameters.covertMaskDepth[0]));
    const covertMaskHz = Math.max(0.05, Math.min(0.5, parameters.covertMaskHz[0]));
    // Phase coherence (Black Book §5.5): 0..1. When >0, the bass pulse and
    // covert mask are phase-locked to the master binaural beat phase so every
    // layer crests together, reinforcing a single frequency-following response.
    const phaseCoherence = Math.max(0, Math.min(1, parameters.phaseCoherence[0]));
    const beatRateTrajectoryInc = (2 * Math.PI) / (beatRateTrajectoryPeriodSeconds * sampleRate);
    const commonInc = (2 * Math.PI * carrierShiftHz) / sampleRate;  // shared relocation
    const breathInc = (2 * Math.PI) / (breathCycleSeconds * sampleRate);
    const bassInc = (2 * Math.PI * bassPulseHz) / sampleRate;
    const covertInc = (2 * Math.PI * covertMaskHz) / sampleRate;
    // Envelope follower time constants (per-sample coefficients). Attack is
    // fast (follows the moan's rise), release is slower (lets the swell decay
    // naturally) so the contour reads as a pulse, not a buzz.
    const envAttack = Math.exp(-1 / (envelopeAttackMs * sampleRate / 1000));
    const envRelease = Math.exp(-1 / (envelopeReleaseMs * sampleRate / 1000));
    const channels = input.length;
    const frames = left.length;

    for (let n = 0; n < frames; n += 1) {
      const instantaneousShiftHz = Math.max(
        0,
        Math.min(40, shiftHz + beatRateTrajectoryDepthHz * Math.sin(this.beatRateTrajectoryPhase)),
      );
      const diffInc = (Math.PI * instantaneousShiftHz) / sampleRate; // 2*pi*(shift/2)/sr
      // Mixdown to mono so both ears start from an identical signal.
      let x = 0;
      for (let ch = 0; ch < channels; ch += 1) x += input[ch][n];
      if (channels > 1) x /= channels;

      // Envelope follower: rectified peak with asymmetric attack/release.
      // This captures the moan/cry rhythm as a 0..1 contour.
      const absX = Math.abs(x);
      const envCoeff = absX > this.envelope ? envAttack : envRelease;
      this.envelope = envCoeff * this.envelope + (1 - envCoeff) * absX;
      // Normalize so a typical loud moan maps near 1 without clipping.
      const env = Math.min(1, this.envelope * 4);

      const i = this.runCascade(x, this.coeffsI, this.stateI);
      const qNow = this.runCascade(x, this.coeffsQ, this.stateQ);
      const q = this.qDelay;
      this.qDelay = qNow;

      // SSB heterodyne with net per-ear shift = carrier -/+ shift/2.
      // With this Hilbert pair's sign convention up-shift = I*cos + Q*sin
      // (verified by sim); a negative phase makes the same formula down-shift
      // because cos is even and sin is odd.
      const phaseL = this.phaseCommon - this.phaseDiff;
      const phaseR = this.phaseCommon + this.phaseDiff + phaseOffset;
      const leftShifted = i * Math.cos(phaseL) + q * Math.sin(phaseL);
      const rightShifted = i * Math.cos(phaseR) + q * Math.sin(phaseR);

      // Beat-locked tremolo: interaural beat phase is 2*phaseDiff, so this
      // dip is exactly coherent with the binaural beat cycle. Carrier
      // relocation does not affect it.
      const ripplePhase = 2 * this.phaseDiff + phaseOffset;
      const normalized = ((ripplePhase / (2 * Math.PI)) % 1 + 1) % 1;
      let wave = Math.cos(ripplePhase);
      if (waveShape === 1) wave = 1 - 4 * Math.abs(normalized - 0.5);
      else if (waveShape === 2) wave = normalized < 0.5 ? 1 : -1;
      else if (waveShape === 3) wave = 1 - 2 * normalized;
      // Blend the fixed LFO with the source envelope: at envelopeEntrainment=1
      // the moan rhythm fully drives the ripple; at 0 it is the fixed theta.
      const drive = (1 - envelopeEntrainment) * wave + envelopeEntrainment * (2 * env - 1);
      const rippleGain = 1 - amDepth * (0.5 - 0.5 * drive);
      const breathGain = 1 - breathDepth * (0.5 + 0.5 * Math.cos(this.breathPhase));
      // Phase coherence: the master beat phase is 2*phaseDiff. When coherence
      // is on, the bass pulse and covert mask are derived from it (scaled to
      // their own rates) so every layer crests together. This reinforces a
      // single frequency-following response instead of letting independent
      // oscillators drift and fight each other (Black Book §5.5).
      const masterPhase = 2 * this.phaseDiff;
      const bassPhase = phaseCoherence > 0
        ? masterPhase * (bassPulseHz / Math.max(0.5, instantaneousShiftHz))
        : this.bassPhase;
      const covertPhase = phaseCoherence > 0
        ? masterPhase * (covertMaskHz / Math.max(0.5, instantaneousShiftHz))
        : this.covertPhase;
      // The same authored BPM that gates the low-bass pulse now leaves a
      // shallow, bounded imprint on the transformed subliminal. This never
      // boosts above unity: peaks reach 1, troughs fall by at most 30%.
      const bassRippleGain = 1 - bassRippleDepth * (0.5 - 0.5 * Math.sin(bassPhase));
      // Covert-mask bulge: a slow swell on the common carrier so the combined
      // base "breathes" under the music bed. Bounded to never boost above
      // unity; it only dips, keeping the content at the perceptual edge.
      const covertGain = 1 - covertMaskDepth * (0.5 - 0.5 * Math.sin(covertPhase));
      const gain = rippleGain * breathGain * bassRippleGain * covertGain;

      const rawLeft = leftShifted * gain;
      const rawRight = rightShifted * gain;
      this.delayBuffer[this.delayIndex] = rawRight;
      const readIndex = (
        this.delayIndex - stereoDelaySamples + this.delayBuffer.length
      ) % this.delayBuffer.length;
      const delayedRight = this.delayBuffer[readIndex];
      this.delayIndex = (this.delayIndex + 1) % this.delayBuffer.length;
      left[n] = rawLeft * (1 - crossfeed) + delayedRight * crossfeed;
      right[n] = delayedRight * (1 - crossfeed) + rawLeft * crossfeed;

      this.phaseDiff += diffInc;
      if (this.phaseDiff > Math.PI * 2) this.phaseDiff -= Math.PI * 2;
      this.phaseCommon += commonInc;
      if (this.phaseCommon > Math.PI * 2) this.phaseCommon -= Math.PI * 2;
      if (this.phaseCommon < -Math.PI * 2) this.phaseCommon += Math.PI * 2;
      this.breathPhase += breathInc;
      if (this.breathPhase > Math.PI * 2) this.breathPhase -= Math.PI * 2;
      this.bassPhase += bassInc;
      if (this.bassPhase > Math.PI * 2) this.bassPhase -= Math.PI * 2;
      this.covertPhase += covertInc;
      if (this.covertPhase > Math.PI * 2) this.covertPhase -= Math.PI * 2;
      this.beatRateTrajectoryPhase += beatRateTrajectoryInc;
      if (this.beatRateTrajectoryPhase > Math.PI * 2) this.beatRateTrajectoryPhase -= Math.PI * 2;
    }
    return true;
  }
}
registerProcessor("${WORKLET_NAME}", SubliminalBinauralShifter);
`;

// One load per AudioContext; resolves true when the processor is registered.
const moduleLoads = new WeakMap();

export function ensureSubliminalWorklet(ctx) {
  if (!ctx || !ctx.audioWorklet || typeof ctx.audioWorklet.addModule !== "function") {
    return Promise.resolve(false);
  }
  let pending = moduleLoads.get(ctx);
  if (pending) return pending;
  let blobUrl = "";
  try {
    blobUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: "application/javascript" }));
  } catch {
    return Promise.resolve(false);
  }
  pending = ctx.audioWorklet
    .addModule(blobUrl)
    .then(() => true)
    .catch((error) => {
      console.warn("[AceLabReact] subliminal binaural worklet failed to load", error);
      moduleLoads.delete(ctx); // allow a retry on the next cue
      return false;
    })
    .finally(() => {
      try { URL.revokeObjectURL(blobUrl); } catch { }
    });
  moduleLoads.set(ctx, pending);
  return pending;
}

/**
 * Create the shifter node. Call only after ensureSubliminalWorklet(ctx)
 * resolved true. Mono in (auto-mixdown), true stereo out.
 */
export function createSubliminalShifterNode(ctx, { shiftHz = 6, amDepth = 0.22, carrierShiftHz = 0 } = {}) {
  try {
    const node = new AudioWorkletNode(ctx, WORKLET_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 1,
      channelCountMode: "explicit",
      channelInterpretation: "speakers",
    });
    node.parameters.get("shiftHz").value = clampShiftHz(shiftHz);
    node.parameters.get("beatRateTrajectoryDepthHz").value = 0;
    node.parameters.get("beatRateTrajectoryPeriodSeconds").value = 45;
    node.parameters.get("amDepth").value = Math.max(0, Math.min(0.9, Number(amDepth) || 0));
    node.parameters.get("carrierShiftHz").value = clampCarrierShiftHz(carrierShiftHz);
    return node;
  } catch (error) {
    console.warn("[AceLabReact] subliminal shifter node creation failed", error);
    return null;
  }
}

export function clampCarrierShiftHz(hz) {
  const value = Number(hz);
  if (!Number.isFinite(value)) return 0;
  return Math.max(-2000, Math.min(16000, value));
}

/**
 * Resolve the spectral disguise for a subliminal cue.
 *
 * Speech keeps its spectrum in place ("beat" mode) when clarity matters.
 * Explicit "masked" mode uses an 8.6 kHz common carrier for low-salience
 * speech as an acoustic experiment; it is not an inaudible command channel.
 * "masked_texture" is available for explicitly opted-in harmonic textures.
 * "silent" parks material on a near-ultrasonic carrier (delivery-fragile:
 * lossy Bluetooth codecs and older ears attenuate above ~15 kHz).
 */
export function resolveCarrierShiftHz({ mode = "", isSpeech = true, darkShiftHz = -600 } = {}) {
  const normalized = String(mode || "").toLowerCase();
  if (["masked", "low_salience", "spectral_mask"].includes(normalized)) {
    return isSpeech ? 8600 : 0;
  }
  if (["masked_texture", "adult_texture"].includes(normalized)) return 8600;
  if (normalized === "silent") return 14000;
  if (normalized === "dark") return clampCarrierShiftHz(darkShiftHz);
  if (normalized === "beat" || normalized === "off") return 0;
  // Auto favors reliable masked delivery. Spoken material stays in-band but
  // is still filtered, level-limited, beat-shifted, and amplitude-modulated by
  // the dedicated subliminal graph. Near-ultrasonic relocation is retained
  // only as an explicit experiment because many playback chains discard it.
  return isSpeech ? 0 : clampCarrierShiftHz(darkShiftHz);
}

/**
 * Resolve the spectral disguise for the continuous subliminal background
 * (the non-verbal "sex bed" lane). The Director's insight: volume is not the
 * issue — FREQUENCY is. Sex sounds played in their natural band (300 Hz–3 kHz)
 * are consciously recognized as moans no matter how quiet. To make them truly
 * subliminal they must be shifted DOWN into the sub-bass region (20–100 Hz)
 * where they are FELT as a low rumble, not consciously parsed as sex sounds.
 *
 * Bluetooth reproduces down to ~20 Hz, so we target a shift that lands the
 * content in the 20–100 Hz felt band. A moan's formants (300 Hz–3 kHz) shifted
 * down by ~-2000 Hz land at 0–1000 Hz; the lowpass (200 Hz) then keeps only
 * the felt sub-bass, and the highpass (20 Hz) keeps it above the BT floor.
 *
 * Returns the carrier shift in Hz (negative = down-shift).
 */
export function resolveSubliminalBackgroundShiftHz({ mode = "" } = {}) {
  const normalized = String(mode || "").toLowerCase();
  // Explicit "dark" mode: shift the sex bed down into the felt sub-bass.
  if (["dark", "sub_bass", "felt", "subliminal"].includes(normalized)) {
    return -2000; // land formants in the 0–1000 Hz region; lowpass keeps <200 Hz
  }
  // "masked" keeps it in-band but heavily filtered (still audible-ish).
  if (["masked", "low_salience"].includes(normalized)) return -600;
  // Default: shift the sex bed down so it reads as texture, not moans.
  return -2000;
}

export function clampShiftHz(hz) {
  const value = Number(hz);
  // A non-numeric request means "no preference" -> theta default. An explicit
  // 0 means "binaural off" and must stay 0: the old code turned it into a 6 Hz
  // beat, so switching binaural off silently switched it back on.
  if (!Number.isFinite(value)) return 6; // theta default
  if (value <= 0) return 0;
  return Math.max(0.5, Math.min(12, value)); // delta..alpha band
}

/**
 * Resolve a bounded, shared amplitude-ripple depth for subliminal lanes.
 *
 * Both scheduled speech and the continuous background use the same target-Hz
 * oscillator in the worklet. The background gets a modest depth lift because
 * it is the continuous carrier, while the ceiling prevents an attention-
 * grabbing pulse. This is synchronized modulation, not a medical claim.
 */
export function resolveSubliminalRippleDepth(scene = {}, { background = false } = {}) {
  const configured = Number(scene?.subliminalRippleDepth);
  const hasConfiguredDepth = Number.isFinite(configured);
  // An authored zero is the control condition. Do not turn an explicit
  // request to remove the ripple back into a detectable effect with the
  // ordinary-playback floor or the background lane's depth lift.
  if (hasConfiguredDepth && configured <= 0) return 0;
  const base = hasConfiguredDepth ? configured : 0.06;
  // The ripple is an attention cue when it becomes a pronounced tremolo.
  // Keep normal playback shallow; the lab can still make a deliberate,
  // measured change without turning the sub lane into foreground audio.
  if (scene?.brainwaveLabEnabled) return Math.max(0, Math.min(0.18, base));
  const laneDepth = base + (background ? 0.01 : 0);
  return Math.max(0.02, Math.min(0.12, laneDepth));
}

/**
 * Resolve the channel capability that is observable from the Web Audio graph.
 * Physical Bluetooth speaker topology is not exposed reliably by browsers;
 * an unknown graph is therefore not treated as verified stereo.
 *
 * NOTE: this is reported for telemetry and diagnostics only. It must NEVER gate
 * an entrainment layer — see the Headphones Are Assumed law in
 * .github/instructions/acelab-engineering-laws.instructions.md.
 */
export function resolveSubliminalChannelMode({ channelCount, maxChannelCount } = {}) {
  const current = Number(channelCount);
  if (Number.isFinite(current) && current === 1) return "mono";
  if (Number.isFinite(current) && current >= 2) return "stereo";
  const maximum = Number(maxChannelCount);
  if (Number.isFinite(maximum) && maximum === 1) return "mono";
  return "unknown";
}

/** Lock a live subliminal lane to the deck's bounded audio target. */
export function applySubliminalBrainwaveScene(
  routing,
  scene = {},
  now = 0,
  { background = false, carrierShiftHz = null } = {},
) {
  const shifter = routing?.shifter;
  if (!shifter?.parameters) return null;
  const requestedShift = Number(scene?.binHz ?? scene?.targetBrainwaveHz);
  // ensureOverlayLaneRouting always supplies this from the destination. Keep
  // the historical default for isolated callers that build a routing stub.
  const channelMode = routing?.channelMode || "stereo";
  // Headphones/buds are assumed at all times (Director, 2026-10-09: "i do not
  // care about [mono users], assume headphones/buds at all times"). The old
  // `&& channelMode === "stereo"` gate silently disabled the interaural beat
  // whenever the browser reported an unknown or mono destination — common on
  // Bluetooth and on several Android builds — so the binaural layer simply
  // never ran. The worklet degrades gracefully on a mono destination: both
  // ears receive the same signal and the beat survives as the monaural
  // tremolo, which is strictly better than no beat at all.
  const binauralEnabled = scene?.binauralEnabled !== false;
  const shiftHz = binauralEnabled
    ? (scene?.brainwaveLabEnabled
      ? Math.max(0.5, Math.min(40, Number.isFinite(requestedShift) ? requestedShift : 6))
      : clampShiftHz(requestedShift))
    : 0;
  const trajectoryEnabled = scene?.beatRateTrajectoryEnabled === true && binauralEnabled;
  const configuredTrajectoryDepth = Number(scene?.beatRateTrajectoryDepthHz);
  const trajectoryDepthHz = trajectoryEnabled
    ? Math.max(
      0,
      Math.min(5, Number.isFinite(configuredTrajectoryDepth) ? configuredTrajectoryDepth : 0.75),
    )
    : 0;
  const trajectoryPeriodSeconds = Math.max(
    30,
    Math.min(60, Number(scene?.beatRateTrajectoryPeriodSeconds) || 45),
  );
  const rippleDepth = resolveSubliminalRippleDepth(scene, { background });
  const breathCycleSeconds = Math.max(4, Math.min(20,
    // Number(x || 3) is NOT NaN-safe: a truthy non-numeric string resolves to
    // itself, Number() yields NaN, and Math.min/max propagate NaN rather than
    // clamping it. Coerce each side independently, then sum.
    (Number.isFinite(Number(scene?.breathInSeconds)) ? Number(scene.breathInSeconds) : 3)
    + (Number.isFinite(Number(scene?.breathOutSeconds)) ? Number(scene.breathOutSeconds) : 3)
  ));
  const configuredBreathDepth = Number(scene?.subliminalBreathDepth);
  const breathDepth = Math.max(
    0,
    Math.min(0.5, Number.isFinite(configuredBreathDepth) ? configuredBreathDepth : 0.16),
  );
  const waveform = String(scene?.brainwaveWaveform || "sine").toLowerCase();
  const waveShape = Math.max(0, ["sine", "triangle", "square", "sawtooth"].indexOf(waveform));
  const phaseOffsetDegrees = Math.max(-180, Math.min(180, Number(scene?.brainwavePhaseDegrees) || 0));
  const stereoDelayMs = Math.max(0, Math.min(30, Number(scene?.brainwaveStereoDelayMs) || 0));
  const crossfeed = Math.max(0, Math.min(0.45, Number(scene?.brainwaveCrossfeed) || 0));
  const bassPulseHz = Math.max(0.2, Math.min(2,
    (Number.isFinite(Number(scene?.bpm)) ? Number(scene.bpm) : 36) / 60,
  ));
  const configuredBassRipple = Number(scene?.bassBrainwaveRippleDepth);
  const bassRippleDepth = scene?.bassBrainwaveRippleEnabled === false
    ? 0
    : Number.isFinite(configuredBassRipple)
      ? Math.max(0, Math.min(0.3, configuredBassRipple))
      : 0.12;
  const shiftParam = shifter.parameters.get("shiftHz");
  const depthParam = shifter.parameters.get("amDepth");
  if (!shiftParam || !depthParam) return null;
  const setParam = (name, value, timeConstant = 0.05) => {
    const param = shifter.parameters.get(name);
    if (!param) return;
    // Never let a non-finite value reach an AudioParam: Web Audio throws
    // "The provided float value is non-finite", which aborts this whole pass
    // and can silence the lane. Scenes are server-authored, so a malformed
    // field must degrade to a default instead of killing the session.
    const safe = Number(value);
    const bounded = Number.isFinite(safe) ? safe : 0;
    const tc = Number.isFinite(Number(timeConstant)) ? Number(timeConstant) : 0.05;
    if (typeof param.setTargetAtTime === "function") param.setTargetAtTime(bounded, now, tc);
    else param.setValueAtTime(bounded, now);
  };
  // Smoothing prevents the 15-second protocol refresh from producing a beat
  // step. The worklet's trajectory remains continuous between these updates.
  setParam("shiftHz", shiftHz, 0.8);
  setParam("beatRateTrajectoryDepthHz", trajectoryDepthHz, 0.8);
  setParam("beatRateTrajectoryPeriodSeconds", trajectoryPeriodSeconds, 0.8);
  setParam("amDepth", rippleDepth);
  setParam("breathCycleSeconds", breathCycleSeconds);
  setParam("breathDepth", breathDepth);
  setParam("waveShape", waveShape);
  setParam("phaseOffsetDegrees", phaseOffsetDegrees);
  setParam("stereoDelayMs", stereoDelayMs);
  setParam("crossfeed", crossfeed);
  setParam("bassPulseHz", bassPulseHz);
  setParam("bassRippleDepth", bassRippleDepth);
  // Envelope-following entrainment + covert masking (bounded, default off).
  const envelopeEntrainment = Math.max(0, Math.min(1, Number(scene?.envelopeEntrainment) || 0));
  const envelopeAttackMs = Math.max(1, Math.min(200, Number(scene?.envelopeAttackMs) || 12));
  const envelopeReleaseMs = Math.max(20, Math.min(2000, Number(scene?.envelopeReleaseMs) || 220));
  const covertMaskDepth = Math.max(0, Math.min(0.5, Number(scene?.covertMaskDepth) || 0));
  const covertMaskHz = Math.max(0.05, Math.min(0.5, Number(scene?.covertMaskHz) || 0.15));
  // Phase coherence (Black Book §5.5): lock the bass pulse and covert mask to
  // the master binaural beat phase so all layers crest together, reinforcing a
  // single frequency-following response. Default on for the covert lane.
  const phaseCoherence = Math.max(0, Math.min(1, Number(scene?.phaseCoherence) ?? 1));
  setParam("envelopeEntrainment", envelopeEntrainment, 0.8);
  setParam("envelopeAttackMs", envelopeAttackMs);
  setParam("envelopeReleaseMs", envelopeReleaseMs);
  setParam("covertMaskDepth", covertMaskDepth, 0.8);
  setParam("covertMaskHz", covertMaskHz);
  setParam("phaseCoherence", phaseCoherence, 0.8);
  const requestedCarrier = scene?.brainwaveLabEnabled
    ? scene?.brainwaveCarrierShiftHz
    : carrierShiftHz;
  if (requestedCarrier !== null && requestedCarrier !== undefined) {
    setParam("carrierShiftHz", clampCarrierShiftHz(requestedCarrier));
  }
  // The continuous background ("sex bed") is shifted DOWN into the felt
  // sub-bass (20–100 Hz) so it is FELT, not consciously parsed as moans.
  // Chain order matters: the PRE-shift lowpass must stay wide (2000 Hz) so the
  // moan formants (300 Hz–3 kHz) reach the shifter, which brings them down;
  // then the POST-shift subBassLowpass at 200 Hz strips the recognizable
  // content, leaving only the felt rumble. A pre-shift lowpass at 200 Hz would
  // destroy the formants before the shift could relocate them.
  const isSubBassBackground = background && Math.abs(Number(carrierShiftHz) || 0) >= 1500;
  const highpassHz = isSubBassBackground
    ? 20
    : Math.max(20, Math.min(1200, Number(scene?.brainwaveHighpassHz) || 240));
  const lowpassHz = Math.max(1200, Math.min(18000, Number(scene?.brainwaveLowpassHz) || 2000));
  routing.highpass?.frequency?.setTargetAtTime?.(highpassHz, now, 0.05);
  routing.lowpass?.frequency?.setTargetAtTime?.(lowpassHz, now, 0.05);
  // Post-shift sub-bass lowpass: only active for the sub-bass background. When
  // inactive, bypass it (set to max so it passes everything).
  if (routing.subBassLowpass?.frequency) {
    const subBassHz = isSubBassBackground ? 200 : 20000;
    routing.subBassLowpass.frequency.setTargetAtTime(subBassHz, now, 0.05);
  }
  const subliminalBaseGain = resolveSubliminalGain(scene, { background });
  if (routing.gain?.gain) {
    const boundedGain = subliminalBaseGain;
    routing.subliminalBaseGain = boundedGain;
    if (typeof routing.gain.gain.setTargetAtTime === "function") {
      routing.gain.gain.setTargetAtTime(boundedGain, now, 0.08);
    } else {
      routing.gain.gain.setValueAtTime(boundedGain, now);
    }
  }
  const appliedScene = {
    shiftHz, rippleDepth, breathCycleSeconds, breathDepth, waveform,
    channelMode, binauralEnabled, trajectoryEnabled, trajectoryDepthHz, trajectoryPeriodSeconds,
    phaseOffsetDegrees, stereoDelayMs, crossfeed, bassPulseHz,
    bassRippleDepth, highpassHz, lowpassHz, subliminalBaseGain,
    envelopeEntrainment, envelopeAttackMs, envelopeReleaseMs, covertMaskDepth, covertMaskHz,
    phaseCoherence,
  };
  routing.lastAppliedSubliminalScene = appliedScene;
  return appliedScene;
}
